// Página de status de integração com SaaS específico (owltask, reservei, vizzy).
// Substitui PlaceholderPage por algo decente: mostra produto SaaS, contagem de
// clientes vinculados, última run de bootstrap, link pra tela completa.

import { useMemo } from 'react';
import { Boxes, Plug } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PillButton } from '../../components/clients/PillButton';
import { useSaasProducts } from '../../hooks/use-saas-products';

interface SaasStatusPageProps {
  slug: string;
  fallbackName: string;
  description: string;
  notes?: string[];
}

export function SaasStatusPage({
  slug,
  fallbackName,
  description,
  notes,
}: SaasStatusPageProps): JSX.Element {
  const saas = useSaasProducts({ limit: 100 });
  const product = useMemo(() => saas.data?.items.find((p) => p.slug === slug), [saas.data, slug]);

  return (
    <div className="space-y-5">
      <header className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] bg-panel-2 text-muted">
          <Boxes size={20} strokeWidth={1.6} />
        </span>
        <div>
          <h1 className="m-0 text-[24px] font-bold tracking-[-0.025em] text-ink">
            {product?.name ?? fallbackName}
          </h1>
          <p className="mt-1 text-[13px] text-muted">{description}</p>
        </div>
      </header>

      {saas.isLoading && <p className="text-[13px] text-muted">Carregando status…</p>}

      {!saas.isLoading && !product && (
        <div className="rounded-[14px] border border-warn/40 bg-warn/5 p-5">
          <h2 className="text-[14px] font-medium text-ink">SaaS não cadastrado</h2>
          <p className="mt-1 text-[12px] text-muted">
            Esse SaaS ainda não tem produto cadastrado no Control Center. Cadastre em{' '}
            <Link to="/catalog/products" className="text-info hover:underline">
              Catálogo &rarr; SaaS Products
            </Link>{' '}
            antes de conectar.
          </p>
        </div>
      )}

      {product && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatBlock label="Status" value={product.status} />
          <StatBlock label="Slug" value={product.slug} mono />
          <StatBlock label="Nome" value={product.name} />
        </div>
      )}

      <section className="rounded-[14px] border border-line bg-panel p-5">
        <h2 className="mb-3 text-[13px] font-semibold text-ink">Próximos passos</h2>
        <ul className="space-y-2 text-[12px] text-muted">
          {(notes ?? []).map((n, i) => (
            <li key={i}>· {n}</li>
          ))}
        </ul>

        <div className="mt-5 flex flex-wrap gap-2 border-t border-line/40 pt-4">
          <PillButton
            solid
            icon={<Plug size={14} strokeWidth={2} />}
            onClick={() => (window.location.href = '/integrations')}
          >
            Ver todas as integrações
          </PillButton>
          <PillButton onClick={() => (window.location.href = '/catalog/products')}>
            Catálogo SaaS Products
          </PillButton>
        </div>
      </section>
    </div>
  );
}

function StatBlock({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: string;
  mono?: boolean;
  small?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-[10px] border border-line bg-panel p-4">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-3">{label}</div>
      <div
        className={
          mono
            ? `mt-1 font-mono text-ink-2 ${small ? 'text-[11px]' : 'text-[13px]'} truncate`
            : 'mt-1 text-[15px] font-semibold text-ink'
        }
      >
        {value}
      </div>
    </div>
  );
}

// Pages concretas pra cada integração
export function IntegrationOwlTaskPage(): JSX.Element {
  return (
    <SaasStatusPage
      slug="owltask"
      fallbackName="OwlTask"
      description="Gestão de tarefas, projetos e equipes. Integração via APIs /internal/v1/* + webhooks outbound."
      notes={[
        'OwlTask precisa implementar APIs /internal/v1/* no repo cross-project (Epic E9).',
        'Quando staging OwlTask estiver pronto: Stories 9.1, 9.3 e 9.4 destravam aqui.',
        'Hoje você pode usar /identities/import pra import manual SaaS-direct.',
      ]}
    />
  );
}

export function IntegrationReserveiPage(): JSX.Element {
  return (
    <SaasStatusPage
      slug="reservei"
      fallbackName="Reservei.app"
      description="Agendamento pra clínicas de estética. Bridge de identidades via client_saas_links + bootstrap inicial."
      notes={[
        'Bridge de identidades já cobertura em E8 (saas_direct webhook).',
        'Bootstrap manual via /identities/import disponível.',
        'Stories cross-repo Wave 2 vão automatizar provisioning + cancelamento.',
      ]}
    />
  );
}

export function IntegrationVizzyPage(): JSX.Element {
  return (
    <SaasStatusPage
      slug="vizzy"
      fallbackName="Vizzy AI"
      description="Plataforma de IA conversacional. Integração agendada pra fase posterior."
      notes={[
        'Sem escopo definido — DR-016 marca como Sob demanda.',
        'Bootstrap manual via /identities/import disponível quando precisar.',
        'Aguardando produto Vizzy expor APIs /internal/v1/*.',
      ]}
    />
  );
}
